"use client";

import { CalendarIcon } from "@radix-ui/react-icons";
import { addDays, format, isSameDay } from "date-fns";
import { motion } from "framer-motion";
import { XIcon } from "lucide-react";
import * as React from "react";
import {
  useCallback,
  useMemo,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";
import { type DateRange } from "react-day-picker";

import { cn } from "../lib/utils";
import { Button } from "./button";
import { Calendar } from "./calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";

export function DatePickerWithRange({
  dateRange,
  setDateRange,
  className,
}: React.HTMLAttributes<HTMLDivElement> & {
  dateRange: DateRange | undefined;
  setDateRange: Dispatch<SetStateAction<DateRange | undefined>>;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [hoveredDay, setHoveredDay] = useState<Date | undefined>(undefined);

  const handleSelect = useCallback(
    (range: DateRange | undefined) => {
      setDateRange(range);
      // Close popper if a start and end date are selected
      if (range?.from && range?.to) {
        setIsOpen(false);
      }
    },
    [setDateRange]
  );

  // Function to handle day hover
  const handleDayHover = (day: Date | undefined) => {
    setHoveredDay(day);
  };

  const hoverRange = useMemo(() => {
    if (
      dateRange == null ||
      hoveredDay == null ||
      (dateRange.from != null && isSameDay(dateRange.from, hoveredDay)) ||
      (dateRange.to != null && isSameDay(dateRange.to, hoveredDay))
    ) {
      return undefined;
    }

    if (dateRange.from && dateRange.to == null) {
      if (hoveredDay > dateRange.from) {
        return { from: addDays(dateRange.from, 1), to: hoveredDay };
      } else {
        return { from: hoveredDay, to: addDays(dateRange.from, -1) };
      }
    } else if (dateRange.from && dateRange.to) {
      if (hoveredDay < dateRange.from) {
        return { from: hoveredDay, to: addDays(dateRange.to, -1) };
      } else if (hoveredDay > dateRange.to) {
        return { from: addDays(dateRange.from, 1), to: hoveredDay };
      }
    }
    return undefined;
  }, [dateRange, hoveredDay]);

  // Combine the selected range and hover range
  const modifiers =
    hoverRange && dateRange
      ? {
          hoverRange: hoverRange,
          selectedRange: dateRange,
        }
      : {};

  // Define styles for the hover effect
  const modifiersStyles = {
    hoverRange: {
      backgroundColor: "#f4f4f5",
      color: "#000",
    },
    dayRangeStart: {
      backgroundColor: "#000",
      color: "#fff",
    },
    dayRangeEnd: {
      backgroundColor: "#000",
      color: "#fff",
    },
  };

  return (
    <div className={cn("grid gap-2", className)}>
      <Popover
        open={isOpen}
        onOpenChange={(e) => {
          // Delete date range if user does not select an end date
          if (!e) {
            if (dateRange?.from && dateRange?.to == null) {
              setDateRange(undefined);
            }
          }
          setIsOpen(e);
        }}
      >
        <PopoverTrigger asChild>
          <Button
            id="date"
            variant="secondary"
            className={cn(
              "flex items-center justify-between text-left font-normal"
            )}
          >
            <div className="flex items-center gap-1">
              <CalendarIcon className="mr-1.5 h-4 w-4" />
              {dateRange?.from ? (
                dateRange.to ? (
                  <>
                    {format(dateRange.from, "LLL dd, y")} -{" "}
                    {format(dateRange.to, "LLL dd, y")}
                  </>
                ) : (
                  <>
                    {format(dateRange.from, "LLL dd, y")}
                    {
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 1, duration: 0.5 }}
                      >
                        -{" "}
                      </motion.div>
                    }
                  </>
                )
              ) : (
                <span>Date Range</span>
              )}
            </div>
            {dateRange && (
              <div
                className="relative -right-1 rounded-md border bg-white p-1 text-xs"
                onClick={(e) => {
                  e.preventDefault();
                  setDateRange(undefined);
                }}
              >
                <XIcon className="h-2.5 w-2.5" />
              </div>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            initialFocus
            mode="range"
            defaultMonth={dateRange?.from}
            selected={dateRange}
            onSelect={handleSelect}
            numberOfMonths={2}
            disabled={(date) =>
              date > new Date() || date < new Date("2023-01-01")
            }
            onDayMouseEnter={handleDayHover}
            onDayMouseLeave={() => setHoveredDay(undefined)}
            {...(modifiers && { modifiers })}
            modifiersStyles={modifiersStyles}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
